import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';
import * as routes from 'router/Routes';

import Card from 'components/UI/Card';
import CardHeader from 'components/UI/Card/CardHeader';
import { CardHeaderTop, CardHeaderTopColumn } from 'pages/Software/styled';
import InfoItem from 'components/UI/InfoItem';
import { ContentContainer, NameContainer, DetailGroup } from './styled';
import Loader from 'components/UI/Loader';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import { Rollout } from 'models/rollout';
import Progressbar from 'components/UI/Progressbar';
import { DetailTitle } from 'components/UI/InfoItem/styled';

type Props = {
  rollout: Rollout | null;
  isLoading: boolean;
};

const RolloutInfoCard: FC<Props> = ({ rollout, isLoading }) => {
  const intl = useIntl();

  // Details
  const rolloutDetails = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
    if (!rollout) {
      return null;
    }
    const {
      createdAt,
      software,
      progress,
      status,
      iprIds,
      pnc,
      excludeTestDevices,
    } = rollout.attributes;

    const includedDevicesText = iprIds
      ? `IprIds: ${iprIds}`
      : pnc && excludeTestDevices
      ? `PNC ${pnc}, excluded test devices`
      : pnc
      ? `PNC ${pnc}`
      : 'unknown filter';
    // Detail list
    const rolloutList = [
      {
        id: 1,
        title: 'rollout.software',
        text: software,
        conditional: false,
      },
      {
        id: 2,
        title: 'rollout.status',
        text: status,
        conditional: false,
      },
      {
        id: 3,
        title: 'rollout.includedDevices',
        text: includedDevicesText,
        conditional: false,
      },
      {
        id: 4,
        title: 'rollout.created_date',
        text: createdAt
          ? format(Number(createdAt), 'yyyy-MM-dd HH:mm:ss')
          : '-',
        conditional: false,
      },
    ];

    const rolloutAttributes = rolloutList.map(
      ({ id, title, text, conditional }) => {
        if (!text && conditional) {
          return null;
        }
        return (
          <InfoItem
            key={id}
            title={intl.formatMessage({ id: title })}
            value={text || '–'}
          />
        );
      }
    );
    return (
      <NameContainer>
        <DetailGroup>{rolloutAttributes}</DetailGroup>
        <DetailTitle>
          {intl.formatMessage({ id: 'rollout.progress' })}
        </DetailTitle>
        <Progressbar
          succeeded={progress.succeeded}
          failed={progress.failed}
          waiting={progress.waiting}
          notStarted={progress.notStarted}
          cancelled={progress.cancelled}
          scheduled={progress.scheduled}
        />
      </NameContainer>
    );
  }, [intl, rollout, isLoading]);

  return (
    <Card>
      <CardHeader navUrl={routes.ROLLOUTS} title="rollout.btn_back" />
      <CardHeaderTop>
        <CardHeaderTopColumn>
          <Heading type={HeadingType.h3} uppercase>
            {`${rollout?.attributes.name}`}
          </Heading>
        </CardHeaderTopColumn>
      </CardHeaderTop>
      <ContentContainer>{rolloutDetails}</ContentContainer>
    </Card>
  );
};

export default RolloutInfoCard;
